import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Paper, Button, TextField, InputAdornment, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import axios from 'axios';
import { USER } from '../../api/vsc';
import RefferalTable from '../auth/referralTable';
import './index.css';

const Referral = () => {
  const [userData, setUserData] = useState({});
  const [successfulReferral, setSuccessfulReferral] = useState(0);

  useEffect(() => {
    axios
      .get(USER, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        setUserData(response.data);
        const successfulOnes = response.data.referredEmails?.filter(
          (referral) => referral.paid === true
        ) || [];
        setSuccessfulReferral(successfulOnes.length);
      })
      .catch((error) => {
        console.error("Referral Fetch Error:", error);
      });
  }, []);

  const referralLink = `https://markoknow.com/signup?referrer=${userData.referralId || ''}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    alert("Link copied to clipboard!");
  };

  return (
    <Box className="db-container">
      {/* 1. TOP SECTION: Header Card */}
      <Paper elevation={0} className="ref-main-card">
        <Typography variant="h4" fontWeight="800" mb={1}>Refer A Friend</Typography>
        <Typography variant="body1" className="ref-desc">
          Help others discover meaningful learning, mentorship and opportunity through Markoknow. 
          Share your referral link and invite people who believe in building employability with purpose.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: '#555' }}>
          Your referral helps them access meaningful knowledge and community.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            className="ref-btn-primary"
            onClick={copyToClipboard}
          >
            Copy Your Referral Link
          </Button>
          
          <Box className="ref-share-box">
            <Typography variant="body2" fontWeight="600">Share on</Typography>
            <IconButton color="success"><WhatsAppIcon /></IconButton>
            <IconButton sx={{ color: '#E4405F' }}><InstagramIcon /></IconButton>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3} mt={1}>
        {/* 2. BOTTOM LEFT: How Referral Works */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} className="ref-sub-card">
            <Typography variant="h5" fontWeight="800" mb={3}>How Referral Works</Typography>
            
            <Box className="ref-step">
              <Typography variant="subtitle1" fontWeight="700">1. Share your referral link</Typography>
              <Typography variant="body2" color="textSecondary">
                Copy your unique referral link from your dashboard and share it with friends, 
                colleagues, or anyone who values growth.
              </Typography>
              
              <TextField
                fullWidth
                value={referralLink}
                variant="outlined"
                sx={{ mt: 2 }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button 
                        startIcon={<ContentCopyIcon />} 
                        className="ref-inline-copy"
                        onClick={copyToClipboard}
                      >
                        Copy
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className="ref-step" mt={3}>
              <Typography variant="subtitle1" fontWeight="700">2. They Apply for Membership</Typography>
              <Typography variant="body2" color="textSecondary">
                Your friend uses your link to apply for Markoknow General Membership. 
              </Typography>
            </Box>

            <Box className="ref-step" mt={3}>
              <Typography variant="subtitle1" fontWeight="700">3. Grow the community</Typography>
              <Typography variant="body2" color="textSecondary">
                Once they join, they become part of the Markoknow ecosystem.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* 3. BOTTOM RIGHT: Best Practices */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} className="ref-sub-card">
            <Typography variant="h5" fontWeight="800" mb={3}>Best Practices for Inviting Friends</Typography>
            {[
              { title: "Invite People Who Value Learning", desc: "Share your link with individuals who are curious and growth-oriented." },
              { title: "Share Your Personal Experience", desc: "Tell your friends why you joined Markoknow and how it helped you." },
              { title: "Explain the Purpose of the Community", desc: "Help others understand that Markoknow focuses on employability." },
              { title: "Encourage Thoughtful Participation", desc: "Invite people who want to learn, contribute, and engage." }
            ].map((item, i) => (
              <Box key={i} mb={3}>
                <Typography variant="subtitle1" fontWeight="700">{item.title}</Typography>
                <Typography variant="body2" color="textSecondary">{item.desc}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* 4. REFERRAL HISTORY: The Table Component */}
      <Box mt={4}>
        <Typography variant="h5" fontWeight="800" mb={2}>Referral History</Typography>
        <RefferalTable externalUser={userData} />
      </Box>
    </Box>
  );
};

export default Referral;